/**
 * i18n — UI language, Japanese and English.
 *
 * Deliberately tiny and synchronous. There is no loader, no async bundle and no
 * pluralisation engine, because this is a shooter HUD: a few hundred short labels
 * that all have to be on screen the instant a frame is drawn. A translation that
 * arrives one frame late is a HUD that flickers from English to Japanese in front of
 * the player.
 *
 * ## What is *not* translated, on purpose
 *
 * Weapon names, attachment names and their flavour text. Those are the roster the
 * player supplied, they read as proper nouns, and a shooter that calls a gun by a
 * different name depending on the menu language is a shooter whose community cannot
 * talk to itself. Same reason real games leave them alone.
 *
 * ## How a screen uses this
 *
 * Two ways, and both are cheap:
 *
 *   - `t('touch.fire')` when building a string in code;
 *   - `data-i18n="touch.fire"` on an element, then `applyI18n(root)` after painting.
 *     Static markup then costs one attribute per string instead of a rewrite.
 *
 * Screens that can be open when the language changes subscribe with `onLang()` and
 * re-render. Screens that are rebuilt each time they open need nothing.
 */

const STORE_KEY = 'ob.lang';

export const LANGS = { ja: '日本語', en: 'English' };

/**
 * Every UI string, flat, keyed by dotted path.
 *
 * Flat rather than nested because a missing key must be obvious: `t()` returns the
 * key itself when it cannot find a translation, so a typo shows up on screen as
 * `hud.helth` rather than as an empty box that looks like a layout bug.
 */
const STRINGS = {
  ja: {
    /* --- touch controls --- */
    'touch.fire': '射撃',
    'touch.ads': 'エイム',
    'touch.reload': 'リロード',
    'touch.jump': 'ジャンプ',
    'touch.crouch': 'しゃがむ',
    'touch.sprint': 'スプリント',
    'touch.swap': '武器切替',
    'touch.grenade': 'グレネード',
    'touch.tactical': 'タクティカル',
    'touch.melee': 'ナイフ',
    'touch.pause': 'メニュー',
    'touch.rotate': '横向きにすると遊びやすい',
    'touch.rotateSkip': 'このまま遊ぶ',
    'touch.editHint': 'ボタンをドラッグして配置を変える',
    'touch.editReset': '初期配置に戻す',
    'touch.editDone': '完了',

    /* --- HUD --- */
    'hud.health': '体力',
    'hud.kills': 'キル',
    'hud.xp': 'XP',
    'hud.headshot': 'ヘッドショット',

    /* --- pause / menus --- */
    'menu.paused': '一時停止',
    'menu.pausedSub': 'ミッション中断',
    'menu.resume': '再開',
    'menu.restart': '最初から',
    'menu.sensitivity': '感度',
    'menu.quality': '解像度',
    'menu.shadows': 'シャドウ',
    'menu.stats': 'スタッツ',
    'menu.language': '言語',
    'menu.layout': 'ボタン配置',
    'menu.layoutEdit': '編集する',
    'menu.on': 'オン',
    'menu.off': 'オフ',
    'menu.dead': 'ダウン',
    'menu.deadSub': 'リスポーン待機中',
    'menu.respawn': 'リスポーン',

    /* --- start screen --- */
    'start.deploy': 'タップして出撃',
    'start.deployKey': 'クリックまたは任意のキーで出撃',

    /* --- loadout / gunsmith commands --- */
    'cmd.deploy': '出撃 ▶',
    'cmd.close': '◀ 閉じる',
    'cmd.back': '◀ 戻る',
    'cmd.equip': '装備する',
    'cmd.gunsmith': 'ガンスミス',
    'cmd.stats': 'スタッツ',
    'cmd.otherSlot': '別スロット',
    'cmd.fit': '装着 / 解除',
    'cmd.strip': 'スロット解除',
    'cmd.clear': '全解除',
    'cmd.range': '射撃テスト',
    'cmd.builds': 'ビルド',
    'cmd.equipment': 'グレネード/タクティカル',
    'cmd.closeShort': '閉じる',

    /* --- equipment screen --- */
    'eq.title': '装備',
    'eq.level': 'レベル',
    'eq.lethal': 'リーサル',
    'eq.tactical': 'タクティカル',
    'eq.equipped': '装備中',
    'eq.tap': 'タップ',
    'eq.tapHint': 'タップして装備',
    'eq.equip': '装備',
    'eq.switch': '列を切替',
    'eq.pick': '選択',
    'eq.damage': 'ダメージ',
    'eq.dps': '毎秒ダメージ',
    'eq.radius': '効果範囲',
    'eq.duration': '持続時間',
    'eq.heal': '回復量',
    'eq.fuse': 'ヒューズ',
    'eq.cook': 'クック可能',

    /* --- killfeed --- */
    'feed.you': '自分',
    'feed.enemy': 'エネミー',
    'feed.ally': 'アライ',
    
    'feed.eliminated': 'キル',
    'feed.neutralised': '{zone} をニュートラルに',

    /* --- progression / streaks / gunsmith --- */
    'lo.maxRank': 'ランクマックス',
    'lo.maxRankBody': 'アンロックはすべて解放済み。',
    'ss.trayFull': 'トレイが満杯',
    'ss.deployFirst': '先に1つ使う',
    'ss.streaksLost': 'ストリーク喪失',
    'ss.killedInAction': 'ダウンしたため',
    'ss.undeployed': '{n} 個未使用',
    'ss.cycle': '切替',
    'ss.deploy': '使用',
    'ss.tapDeploy': 'タップして使用',
    'gs.points': 'ポイント',
    'gs.expanded': '拡張済み',
    'gs.parts': '{n} パーツ',

    /* --- multiplayer --- */
    'mp.title': 'フレンドマッチ',
    'mp.sub': '部屋を作るか、コードを入れて参加する',
    'mp.name': 'プレイヤー名',
    'mp.createRoom': '部屋を作る',
    'mp.joinRoom': '参加',
    'mp.or': 'または',
    'mp.codePlaceholder': 'コード',
    'mp.roomCode': 'ルームコード',
    'mp.copyLink': 'リンクをコピー',
    'mp.copied': 'コピーした',
    'mp.leave': '退出',
    'mp.working': '接続中…',
    'mp.badCode': 'コードが短すぎる',
    'mp.connected': '接続済み',
    'mp.connecting': '接続中',
    'mp.youHost': 'ホスト',
    'mp.youClient': '参加中',
    'mp.note': '対戦中はボットが止まる。人対人のみ。',
    'cmd.multiplayer': 'フレンドマッチ',

    /* --- banners --- */
    'banner.doubleKill': 'ダブルキル',
    'banner.rampage': 'ランページ',
    'banner.missionComplete': 'ミッションコンプリート',
    'banner.victory': 'ビクトリー',
    'banner.defeat': 'ディフィート',
    'banner.draw': 'ドロー',
    'banner.roundWon': 'ラウンド獲得',
    'banner.roundLost': 'ラウンド喪失',
    'banner.attack': 'アタック',
    'banner.defend': 'ディフェンス',
    'banner.bombPlanted': '{zone} に爆弾を設置',
    'banner.bombEnemy': '{zone} で爆弾が起動',
    'banner.captured': '{zone} を確保',
    'banner.lost': '{zone} を奪われた',

    /* --- mode select --- */
    'ms.title': 'マッチ設定',
    'ms.sub': 'モードとボットの強さを選ぶ',
    'ms.mode': 'ゲームモード',
    'ms.skill': 'ボットの強さ',
    'ms.squad': 'チーム人数',
    'ms.apply': 'この設定で始める',
    'cmd.modes': 'マッチ設定',
  },

  en: {
    'touch.fire': 'FIRE',
    'touch.ads': 'ADS',
    'touch.reload': 'RELOAD',
    'touch.jump': 'JUMP',
    'touch.crouch': 'CROUCH',
    'touch.sprint': 'RUN',
    'touch.swap': 'SWAP',
    'touch.grenade': 'NADE',
    'touch.tactical': 'TAC',
    'touch.melee': 'KNIFE',
    'touch.pause': 'MENU',
    'touch.rotate': 'ROTATE TO LANDSCAPE FOR A BETTER FIT',
    'touch.rotateSkip': 'PLAY ANYWAY',
    'touch.editHint': 'DRAG THE BUTTONS TO REARRANGE THEM',
    'touch.editReset': 'RESET',
    'touch.editDone': 'DONE',

    'hud.health': 'HEALTH',
    'hud.kills': 'KILLS',
    'hud.xp': 'XP',
    'hud.headshot': 'HEADSHOT',

    'menu.paused': 'PAUSED',
    'menu.pausedSub': 'MISSION SUSPENDED',
    'menu.resume': 'RESUME',
    'menu.restart': 'RESTART',
    'menu.sensitivity': 'SENSITIVITY',
    'menu.quality': 'RESOLUTION',
    'menu.shadows': 'SHADOWS',
    'menu.stats': 'STATS',
    'menu.language': 'LANGUAGE',
    'menu.layout': 'BUTTON LAYOUT',
    'menu.layoutEdit': 'EDIT',
    'menu.on': 'ON',
    'menu.off': 'OFF',
    'menu.dead': 'KILLED IN ACTION',
    'menu.deadSub': 'OPERATOR DOWN',
    'menu.respawn': 'REDEPLOY',

    'start.deploy': 'TAP TO DEPLOY',
    'start.deployKey': 'CLICK OR PRESS ANY KEY TO DEPLOY',

    'cmd.deploy': 'DEPLOY ▶',
    'cmd.close': '◀ CLOSE',
    'cmd.back': '◀ BACK',
    'cmd.equip': 'EQUIP',
    'cmd.gunsmith': 'GUNSMITH',
    'cmd.stats': 'STATS',
    'cmd.otherSlot': 'OTHER SLOT',
    'cmd.fit': 'FIT / REMOVE',
    'cmd.strip': 'STRIP SLOT',
    'cmd.clear': 'CLEAR',
    'cmd.range': 'FIRING RANGE',
    'cmd.builds': 'BUILDS',
    'cmd.equipment': 'NADE / TAC',
    'cmd.closeShort': 'CLOSE',

    'eq.title': 'EQUIPMENT',
    'eq.level': 'LEVEL',
    'eq.lethal': 'LETHAL',
    'eq.tactical': 'TACTICAL',
    'eq.equipped': 'EQUIPPED',
    'eq.tap': 'TAP',
    'eq.tapHint': 'TAP A ROW TO EQUIP IT',
    'eq.equip': 'EQUIP',
    'eq.switch': 'SWITCH LIST',
    'eq.pick': 'PICK',
    'eq.damage': 'DAMAGE',
    'eq.dps': 'PER SEC',
    'eq.radius': 'RADIUS',
    'eq.duration': 'DURATION',
    'eq.heal': 'HEAL',
    'eq.fuse': 'FUSE',
    'eq.cook': 'COOKABLE',

    'feed.you': 'YOU',
    'feed.enemy': 'ENEMY',
    'feed.ally': 'ALLY',
    
    'feed.eliminated': 'ELIMINATED',
    'feed.neutralised': '{zone} NEUTRALISED',

    'lo.maxRank': 'MAX RANK',
    'lo.maxRankBody': 'Everything on the ladder is yours.',
    'ss.trayFull': 'TRAY FULL',
    'ss.deployFirst': 'DEPLOY SOMETHING FIRST',
    'ss.streaksLost': 'STREAKS LOST',
    'ss.killedInAction': 'KILLED IN ACTION',
    'ss.undeployed': '{n} UNDEPLOYED',
    'ss.cycle': 'CYCLE',
    'ss.deploy': 'DEPLOY',
    'ss.tapDeploy': 'TAP TO DEPLOY',
    'gs.points': 'POINTS',
    'gs.expanded': 'EXPANDED',
    'gs.parts': '{n} PART{s}',

    'mp.title': 'PLAY WITH FRIENDS',
    'mp.sub': 'Create a room, or enter a code to join one',
    'mp.name': 'PLAYER NAME',
    'mp.createRoom': 'CREATE A ROOM',
    'mp.joinRoom': 'JOIN',
    'mp.or': 'OR',
    'mp.codePlaceholder': 'CODE',
    'mp.roomCode': 'ROOM CODE',
    'mp.copyLink': 'COPY LINK',
    'mp.copied': 'COPIED',
    'mp.leave': 'LEAVE',
    'mp.working': 'CONNECTING…',
    'mp.badCode': 'Code is too short',
    'mp.connected': 'CONNECTED',
    'mp.connecting': 'CONNECTING',
    'mp.youHost': 'HOST',
    'mp.youClient': 'JOINED',
    'mp.note': 'Bots are paused during a match. Humans only.',
    'cmd.multiplayer': 'FRIEND MATCH',

    'banner.doubleKill': 'DOUBLE KILL',
    'banner.rampage': 'RAMPAGE',
    'banner.missionComplete': 'MISSION COMPLETE',
    'banner.victory': 'VICTORY',
    'banner.defeat': 'DEFEAT',
    'banner.draw': 'DRAW',
    'banner.roundWon': 'ROUND WON',
    'banner.roundLost': 'ROUND LOST',
    'banner.attack': 'ATTACK',
    'banner.defend': 'DEFEND',
    'banner.bombPlanted': 'BOMB PLANTED AT {zone}',
    'banner.bombEnemy': 'BOMB ARMED AT {zone}',
    'banner.captured': '{zone} CAPTURED',
    'banner.lost': '{zone} LOST',

    'ms.title': 'MATCH SETUP',
    'ms.sub': 'Pick a mode and how good the bots are',
    'ms.mode': 'MODE',
    'ms.skill': 'BOT SKILL',
    'ms.squad': 'TEAM SIZE',
    'ms.apply': 'START WITH THESE',
    'cmd.modes': 'MATCH SETUP',
  },
};

/** Japanese for a Japanese browser, English otherwise. Overridden by a saved choice. */
function detect() {
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : '';
  return nav.toLowerCase().startsWith('ja') ? 'ja' : 'en';
}

function load() {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return v && STRINGS[v] ? v : null;
  } catch {
    // Private browsing throws on localStorage. A language preference is not worth
    // taking the page down for.
    return null;
  }
}

let current = load() ?? detect();
const listeners = new Set();

/** The active language code. */
export function getLang() {
  return current;
}

/**
 * Translate. Returns the key itself when it is missing, which is deliberate — an
 * untranslated string should be visibly wrong rather than invisibly absent.
 */
export function t(key, params = null) {
  const table = STRINGS[current];
  const s = (table && table[key]) ?? STRINGS.en[key] ?? key;
  if (!params) return s;
  /**
   * `{name}` substitution, and nothing more.
   *
   * Word order differs between the two languages — "A CAPTURED" against
   * "A を確保" — so a line with a value in it cannot be built by concatenating
   * pieces in code without one of the two reading like a translation. The whole
   * sentence lives in the table and the value drops into it.
   */
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in params ? String(params[k]) : m));
}

/** Switch language, persist it, and tell every subscriber to repaint. */
export function setLang(code) {
  if (!STRINGS[code] || code === current) return;
  current = code;
  try {
    localStorage.setItem(STORE_KEY, code);
  } catch {
    /* see load() */
  }
  if (typeof document !== 'undefined') document.documentElement.lang = code;
  for (const fn of listeners) {
    try {
      fn(code);
    } catch (err) {
      // One screen throwing must not stop the rest of the UI from changing language,
      // or a language switch can leave the game half-translated.
      console.warn('[i18n] listener failed', err);
    }
  }
}

/** Subscribe to language changes. Returns an unsubscribe function. */
export function onLang(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Fill in every `[data-i18n]` under `root`.
 *
 * `data-i18n` sets textContent; `data-i18n-attr="placeholder:key"` sets an attribute,
 * for the few controls that need one. Safe to call repeatedly — it is idempotent, so
 * a screen can simply call it at the end of every render.
 */
export function applyI18n(root) {
  if (!root) return;
  const scope = root.querySelectorAll ? root : document;
  for (const el of scope.querySelectorAll('[data-i18n]')) {
    el.textContent = t(el.dataset.i18n);
  }
  for (const el of scope.querySelectorAll('[data-i18n-attr]')) {
    for (const pair of el.dataset.i18nAttr.split(',')) {
      const [attr, key] = pair.split(':');
      if (attr && key) el.setAttribute(attr.trim(), t(key.trim()));
    }
  }
}

if (typeof document !== 'undefined') document.documentElement.lang = current;
